// APD Copilot client calls. Read docs/apd-copilot/FEATURE_CHARTER.md.
//
// There is no send call here, by design: Copilot never sends email. The
// strongest outbound action is createGmailDraft, which places a reviewed draft
// in a Gmail Drafts folder for a person to send from Gmail.

import { api, type ApiError, type TokenGetter, type PartsBaseListingsResponse } from './api';
import type { CopilotMessageView, StreamEvent } from '@shared/copilot/messages';
import type { ResolvedScope, ScopeInput } from '@shared/copilot/scope';
import type { DealState } from '@shared/copilot/deal-state';
import type { HandoffInput, AttentionState, HandoffSummary } from '@shared/copilot/attention';
import type { CandidateEvidence, RelationshipKind, EvidenceGrade, CandidateStatus } from '@shared/copilot/candidates';
import type { DraftKind, IsolationIssue } from '@shared/copilot/drafts';
import type { DraftProposal, WebRecipient } from '@shared/copilot/messages';

export interface CopilotCapability { enabled: boolean; canUse: boolean; canManageSharedMailboxes: boolean; gmailConfigured: boolean }
export interface ThreadView { id: string; scope: ResolvedScope; title: string | null; visibility: 'tenant' | 'private'; createdBy: string | null; createdAt: string; lastMessageAt: string }
export interface HandoffView {
  id: string; attention: AttentionState; label: string; assignee: { membershipId: string | null; name: string | null } | null;
  from: string | null; note: string | null; summary: HandoffSummary | null; summaryByCopilot: boolean; summaryModel: string | null;
  at: string; supersedes: string | null; current: boolean;
}
export interface AttentionItem { id: string; rfq: { publicId: string; ref: string; isAog: boolean }; attention: AttentionState; label: string; mine: boolean; from: string | null; note: string | null; at: string }
export interface CandidateView {
  id: string; subjectIdentifier: string; relationship: RelationshipKind; proposedValue: string; rationale: string | null; grade: EvidenceGrade;
  evidence: CandidateEvidence[]; status: CandidateStatus; note: string | null; proposedByModel: string | null; by: string | null; at: string;
  supersedes: string | null; lineItemId: string | null;
}
export interface DraftView {
  id: string; batchId: string | null; kind: DraftKind; mailbox: { id: string; email: string } | null; to: string[]; cc: string[];
  recipientLabel: string | null; reason: string | null; subject: string; body: string; replyToGmailThreadId: string | null; version: number;
  contentSha256: string; issues: IsolationIssue[]; webRecipients: WebRecipient[]; status: 'draft' | 'gmail_draft_created' | 'discarded'; gmailDraftId: string | null;
  createdBy: string | null; createdAt: string; gmailCreatedAt: string | null; lastError: string | null;
}
export interface MailboxesResponse {
  gmailConfigured: boolean; canManageShared: boolean;
  accessible: Array<{ id: string; email: string; kind: 'personal' | 'shared'; status: string; canDraft: boolean; via: 'owner' | 'grant' }>;
  manageable: Array<{ id: string; email: string; kind: 'personal' | 'shared'; status: string; lastError: string | null; connectedAt: string | null;
    grants: Array<{ id: string; membershipId: string | null; name: string; canRead: boolean; canDraft: boolean }> }>;
}
export interface TeamMember { membershipId: string; role: string; email: string; fullName: string | null; isSelf: boolean }

const key = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);

export const getCopilotCapability = (t: TokenGetter) => api<CopilotCapability>('/api/copilot/capability', { tokenGetter: t });
export const getCopilotModels = (t: TokenGetter) => api<{ auto: boolean; models: string[] }>('/api/copilot/models', { tokenGetter: t });

export function listCopilotThreads(scope: ScopeInput, t: TokenGetter) {
  const q = scope.type === 'workspace' ? 'type=workspace' : `type=${scope.type}&ref=${encodeURIComponent(scope.ref)}`;
  return api<{ scope: ResolvedScope; threads: ThreadView[] }>(`/api/copilot/threads?${q}`, { tokenGetter: t });
}
export const createCopilotThread = (scope: ScopeInput, t: TokenGetter) =>
  api<{ thread: ThreadView }>('/api/copilot/threads', { method: 'POST', tokenGetter: t, body: JSON.stringify({ scope }) });
export const getCopilotThread = (id: string, t: TokenGetter) =>
  api<{ thread: ThreadView; messages: CopilotMessageView[] }>(`/api/copilot/threads/${id}`, { tokenGetter: t });
export const sendCopilotFeedback = (messageId: string, value: 'up' | 'down' | null, t: TokenGetter) =>
  api<void>(`/api/copilot/messages/${messageId}/feedback`, { method: 'POST', tokenGetter: t, body: JSON.stringify({ value }) });

/** Ask, and receive the answer as it streams. Resolves with the saved message. */
export async function askCopilot(threadId: string, body: { text: string; mode?: 'auto' | 'research'; model?: string | null; retryOf?: string | null },
  t: TokenGetter, onEvent: (e: StreamEvent) => void, signal?: AbortSignal): Promise<CopilotMessageView> {
  const token = await t();
  const res = await fetch(`/api/copilot/threads/${threadId}/messages`, {
    method: 'POST', signal,
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    let message = res.statusText || 'Copilot is unavailable.';
    try { message = (await res.json())?.error?.message ?? message; } catch { /* not JSON */ }
    const err: ApiError = { status: res.status, code: 'http_error', message };
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let done: CopilotMessageView | null = null;
  for (;;) {
    const { value, done: end } = await reader.read();
    if (end) break;
    buf += decoder.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const line = chunk.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      const e = JSON.parse(line.slice(6)) as StreamEvent;
      onEvent(e);
      if (e.type === 'done') done = e.message;
      if (e.type === 'error') { const err: ApiError = { status: 502, code: e.retryable ? 'retryable' : 'copilot_error', message: e.message }; throw err; }
    }
  }
  if (!done) { const err: ApiError = { status: 502, code: 'retryable', message: 'The answer was interrupted. Try again.' }; throw err; }
  return done;
}

export const getDealState = (rfqPublicId: string, t: TokenGetter) => api<DealState>(`/api/copilot/rfqs/${rfqPublicId}/deal-state`, { tokenGetter: t });
export const listHandoffs = (rfqPublicId: string, t: TokenGetter) => api<{ handoffs: HandoffView[] }>(`/api/copilot/rfqs/${rfqPublicId}/handoffs`, { tokenGetter: t });
export const recordHandoff = (rfqPublicId: string, body: { input: HandoffInput; expectedCurrent: string | null; fromMessageId?: string | null }, t: TokenGetter) =>
  api<{ id: string }>(`/api/copilot/rfqs/${rfqPublicId}/handoffs`, { method: 'POST', tokenGetter: t, idempotencyKey: key(), body: JSON.stringify(body) });
export const getAttention = (t: TokenGetter) => api<{ items: AttentionItem[] }>('/api/copilot/attention', { tokenGetter: t });

export const listCandidates = (identifier: string, t: TokenGetter) =>
  api<{ candidates: CandidateView[] }>(`/api/copilot/candidates?identifier=${encodeURIComponent(identifier)}`, { tokenGetter: t });
export const recordCandidateFromMessage = (fromMessageId: string, proposalIndex: number, t: TokenGetter) =>
  api<{ candidate: CandidateView }>('/api/copilot/candidates', { method: 'POST', tokenGetter: t, idempotencyKey: key(), body: JSON.stringify({ fromMessageId, proposalIndex }) });
export const reviewCandidate = (id: string, value: 'confirmed' | 'rejected' | 'withdrawn', note: string | undefined, t: TokenGetter) =>
  api<{ candidate: CandidateView }>(`/api/copilot/candidates/${id}/review`, { method: 'POST', tokenGetter: t, idempotencyKey: key(), body: JSON.stringify({ value, ...(note ? { note } : {}) }) });

export const listDrafts = (rfqPublicId: string, t: TokenGetter) => api<{ drafts: DraftView[] }>(`/api/copilot/rfqs/${rfqPublicId}/drafts`, { tokenGetter: t });
export const saveDrafts = (body: { rfqPublicId: string | null; lineItemPublicId?: string | null; mailboxId: string | null; fromMessageId: string | null;
  drafts: Array<Omit<DraftProposal, 'webRecipients'> & { confirmedWebRecipients?: string[] }> }, t: TokenGetter) =>
  api<{ drafts: DraftView[] }>('/api/copilot/drafts', { method: 'POST', tokenGetter: t, idempotencyKey: key(), body: JSON.stringify(body) });
export const editDraft = (id: string, body: { expectedVersion: number; to?: string[]; cc?: string[]; subject?: string; body?: string; mailboxId?: string | null }, t: TokenGetter) =>
  api<{ draft: DraftView }>(`/api/copilot/drafts/${id}`, { method: 'PATCH', tokenGetter: t, body: JSON.stringify(body) });
export const discardDraft = (id: string, t: TokenGetter) => api<{ draft: DraftView }>(`/api/copilot/drafts/${id}/discard`, { method: 'POST', tokenGetter: t, body: '{}' });
/** Places the reviewed draft in Gmail Drafts. Never sends. */
export const createGmailDraft = (id: string, expectedSha256: string, t: TokenGetter) =>
  api<{ draft: DraftView }>(`/api/copilot/drafts/${id}/gmail`, { method: 'POST', tokenGetter: t, body: JSON.stringify({ expectedSha256 }) });

export const getMailboxes = (t: TokenGetter) => api<MailboxesResponse>('/api/copilot/mailboxes', { tokenGetter: t });
export const startGmailConnect = (kind: 'personal' | 'shared', t: TokenGetter) =>
  api<{ url: string }>('/api/copilot/gmail/connect', { method: 'POST', tokenGetter: t, body: JSON.stringify({ kind }) });
export const disconnectMailbox = (id: string, t: TokenGetter) => api<{ ok: true }>(`/api/copilot/mailboxes/${id}/disconnect`, { method: 'POST', tokenGetter: t, body: '{}' });
export const grantMailbox = (id: string, body: { membershipId: string; canRead: boolean; canDraft: boolean }, t: TokenGetter) =>
  api<{ ok: true }>(`/api/copilot/mailboxes/${id}/grants`, { method: 'POST', tokenGetter: t, body: JSON.stringify(body) });
export const revokeMailboxGrant = (id: string, grantId: string, t: TokenGetter) =>
  api<void>(`/api/copilot/mailboxes/${id}/grants/${grantId}`, { method: 'DELETE', tokenGetter: t });

export const getTeamMembers = (t: TokenGetter) => api<{ members: TeamMember[] }>('/api/team/members', { tokenGetter: t });

/** The EXISTING PartsBase live-search route (cache, events and limits apply).
 *  Called only from a button a person presses on a Copilot card. */
export const runPartsBaseSearch = (rfqPublicId: string, lineItemPublicId: string, t: TokenGetter) =>
  api<PartsBaseListingsResponse>(`/api/rfqs/${rfqPublicId}/line-items/${lineItemPublicId}/partsbase-listings`, { tokenGetter: t });
